import React, { useState, useLayoutEffect, useRef } from "react";
import axios from "axios";
import {
  Input,
  Spin,
  Empty,
  Typography,
  Tooltip,
  Avatar,
  Dropdown,
  Menu,
  message,
  Image,
} from "antd";
import {
  SendOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  UserOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
  FileTextOutlined,
  PictureOutlined,
  AudioOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  AimOutlined,
  ShoppingOutlined,
  SnippetsOutlined,
  AppstoreAddOutlined,
  SmileOutlined,
  ClockCircleOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { ButtonItem } from "../../../../../Components/Items";
import { WA } from "../../theme";
import { formatTelefono, inicialesDe, colorAvatar } from "../../../../../Utils/whatsapp.utils";
import ComposerMedia from "../Composers/ComposerMedia";
import ComposerInsumo from "../Composers/ComposerInsumo";
import ComposerUbicacion from "../Composers/ComposerUbicacion";
import SelectorInteractivos from "../Selectores/SelectorInteractivos";
import SelectorPlantillas from "../Selectores/SelectorPlantillas";
import ModalVincularContacto from "../ModalVincularContacto";
import ModalPasarChat from "../ModalPasarChat";
import { PreviewInteractivo } from "../Helpers/interactivosHelpers";
import TextoWhatsapp from "./TextoWhatsapp";

const { Text } = Typography;

if (
  typeof document !== "undefined" &&
  !document.getElementById("wa-img-preview-bg")
) {
  const s = document.createElement("style");
  s.id = "wa-img-preview-bg";
  s.textContent =
    ".ant-image-preview-img{background:#fff;border-radius:4px;}" +
    ".wa-tpl-img.ant-image{width:100%;display:block;}" +
    ".wa-tpl-img .ant-image-img{width:100%;border-radius:8px;}" +
    ".ant-message{z-index:2000 !important;}" +
    ".ant-notification{z-index:2000 !important;}";
  document.head.appendChild(s);
}

if (
  typeof document !== "undefined" &&
  !document.getElementById("wa-input-border")
) {
  const s = document.createElement("style");
  s.id = "wa-input-border";
  s.textContent =
    ".wa-input.ant-input{border-color:#00BFD6;}" +
    ".wa-input.ant-input:hover,.wa-input.ant-input:focus,.wa-input.ant-input-focused{" +
    "border-color:#00BFD6 !important;" +
    "box-shadow:0 0 0 2px rgba(0,191,214,0.2) !important;}";
  document.head.appendChild(s);
}

const nombreConversacion = (conv) => {
  if (conv.contactoId?.nombre) {
    return `${conv.contactoId.nombre} ${conv.contactoId.apellidoPaterno || ""}`.trim();
  }
  return conv.nombrePerfilWa || formatTelefono(conv.telefono);
};

// Texto corto para previsualizar un mensaje citado 
const resumenMensaje = (m) => {
  if (!m) return "";
  switch (m.tipo) {
    case "image":
      return m.contenido?.caption || "📷 Imagen";
    case "sticker":
      return "Sticker";
    case "video":
      return m.contenido?.caption || "🎥 Video";
    case "audio":
      return "🎵 Audio";
    case "document":
      return m.contenido?.filename || "📄 Documento";
    case "location":
      return m.contenido?.name || "📍 Ubicación";
    case "location_request":
      return "📍 Solicitud de ubicación";
    case "interactive":
      return (
        m.contenido?.payload?.body?.text || m.texto || "Mensaje interactivo"
      );
    default:
      return m.texto || "";
  }
};

const Tick = ({ estado, errorMensaje }) => {
  if (estado === "fallido") {
    return (
      <Tooltip title={errorMensaje || "WhatsApp no pudo entregar este mensaje."} zIndex={1300}>
        <CloseCircleOutlined
          style={{ color: "#c0392b", fontSize: 12, marginLeft: 4, cursor: "help" }}
        />
      </Tooltip>
    );
  }
  const leido = estado === "leido";
  const doble = estado === "entregado" || estado === "leido";
  const color = leido ? WA.turquesa : "rgba(0,0,0,0.45)";
  return (
    <span style={{ marginLeft: 4, color, fontSize: 11, whiteSpace: "nowrap" }}>
      <CheckOutlined style={{ fontSize: 11 }} />
      {doble && <CheckOutlined style={{ fontSize: 11, marginLeft: -4 }} />}
    </span>
  );
};

const BurbujaContenido = ({ m, onAbrirArchivo }) => {
  switch (m.tipo) {
    case "image":
    case "sticker":
      return (
        <>
          <Image
            src={m.mediaUrl}
            alt=""
            width={m.tipo === "sticker" ? 120 : 240}
            style={{ borderRadius: 8, background: "#fff" }}
          />
          {m.contenido?.caption && (
            <div style={{ marginTop: 4 }}>
              <TextoWhatsapp texto={m.contenido.caption} />
            </div>
          )}
        </>
      );
    case "video":
      return (
        <>
          <video
            src={m.mediaUrl}
            controls
            style={{ maxWidth: 260, borderRadius: 8, display: "block" }}
          />
          {m.contenido?.caption && (
            <div style={{ marginTop: 4 }}>
              <TextoWhatsapp texto={m.contenido.caption} />
            </div>
          )}
        </>
      );
    case "audio":
      return <audio src={m.mediaUrl} controls style={{ maxWidth: 240 }} />;
    case "document":
      return (
        <>
          <div
            onClick={() => onAbrirArchivo(m.mediaUrl)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              color: "inherit",
            }}
          >
            <FileTextOutlined style={{ fontSize: 22 }} />
            <span style={{ textDecoration: "underline" }}>
              {m.contenido?.filename || "Documento"}
            </span>
          </div>
          {m.contenido?.caption && (
            <div style={{ marginTop: 6 }}>
              <TextoWhatsapp texto={m.contenido.caption} />
            </div>
          )}
        </>
      );
    case "location": {
      const c = m.contenido || {};
      const lat = c.latitude;
      const lng = c.longitude;
      const tieneCoords = lat != null && lng != null;
      const bbox = tieneCoords
        ? `${lng - 0.005},${lat - 0.005},${lng + 0.005},${lat + 0.005}`
        : "";
      return (
        <div>
          {tieneCoords && (
            <iframe
              title="mapa"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`}
              style={{
                width: 240,
                height: 140,
                border: "none",
                borderRadius: 8,
                display: "block",
              }}
            />
          )}
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              marginTop: 6,
              color: WA.turquesa,
            }}
          >
            <EnvironmentOutlined />
            <span>{c.name || c.address || "Ver ubicación"}</span>
          </a>
          {c.address && c.address !== c.name && (
            <div
              style={{ fontSize: 12, color: WA.textSecondary, marginTop: 2 }}
            >
              {c.address}
            </div>
          )}
        </div>
      );
    }
    case "location_request":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AimOutlined style={{ fontSize: 18 }} />
          <div>
            <div style={{ fontWeight: 600 }}>Solicitud de ubicación</div>
            {m.contenido?.texto && (
              <div style={{ fontSize: 12 }}>
                <TextoWhatsapp texto={m.contenido.texto} />
              </div>
            )}
          </div>
        </div>
      );
    case "interactive":
      if (m.contenido?.payload) {
        return (
          <PreviewInteractivo
            tipo={m.contenido.tipoInteractivo}
            payload={m.contenido.payload}
          />
        );
      }
      return <TextoWhatsapp texto={m.texto} />;
    case "template": {
      const render = m.contenido?.render || {};
      return (
        <div style={m.mediaUrl ? { minWidth: 240 } : undefined}>
          {m.mediaUrl && /\.pdf($|\?)/i.test(m.mediaUrl) ? (
            <div
              onClick={() => onAbrirArchivo(m.mediaUrl)}
              style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "inherit", marginBottom: 6 }}
            >
              <FileTextOutlined style={{ fontSize: 22 }} />
              <span style={{ textDecoration: "underline" }}>Documento adjunto</span>
            </div>
          ) : m.mediaUrl ? (
            <Image
              src={m.mediaUrl}
              alt=""
              wrapperClassName="wa-tpl-img"
              style={{ borderRadius: 8, background: "#fff", marginBottom: 6 }}
            />
          ) : null}
          {render.headerTexto && (
            <div style={{ fontWeight: 700, marginBottom: 2 }}>{render.headerTexto}</div>
          )}
          <TextoWhatsapp texto={m.texto} />
          {render.footerTexto && (
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
              {render.footerTexto}
            </Text>
          )}
          {(render.botones || []).map((b, i) => (
            <div
              key={i}
              style={{
                marginTop: 6,
                padding: "5px 10px",
                textAlign: "center",
                border: `1px solid ${WA.border}`,
                borderRadius: 6,
                background: WA.surface,
                color: WA.textPrimary,
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {b.text}
            </div>
          ))}
        </div>
      );
    }
    default:
      return <TextoWhatsapp texto={m.texto} />;
  }
};

const HiloMensajes = ({
  conversacion,
  mensajes,
  loading,
  enviando,
  onEnviar,
  onEnviarMedia,
  onEnviarServicio,
  onEnviarPlantilla,
  headersToken,
  empresaId,
  puedeGuardarPlantilla,
  puedeGuardarImagenInsumo,
  hayMas,
  cargandoMas,
  onCargarMas,
  onVincularContacto,
  numeroPorUsuario,
  puedeDerivar,
  puedeEscribir,
  onDerivar,
}) => {
  const [texto, setTexto] = useState("");
  const [composer, setComposer] = useState(null);
  const [selectorInteractivos, setSelectorInteractivos] = useState(false);
  const [selectorPlantillas, setSelectorPlantillas] = useState(false);
  const [modalVincular, setModalVincular] = useState(false);
  const [modalPasar, setModalPasar] = useState(false);
  const listaRef = useRef(null);
  const convIdRef = useRef(null);
  const prependRef = useRef(null); 
  const distanciaFondoRef = useRef(0); 

  
  useLayoutEffect(() => {
    const cont = listaRef.current;
    if (!cont || loading) return;
    const convId = conversacion?._id;

    if (convIdRef.current !== convId) {
      convIdRef.current = convId;
      prependRef.current = null;
      cont.scrollTop = cont.scrollHeight; 
      return;
    }
    if (prependRef.current != null) {
      cont.scrollTop += cont.scrollHeight - prependRef.current;
      prependRef.current = null;
      return;
    }
    if (distanciaFondoRef.current < 120) {
      cont.scrollTo({ top: cont.scrollHeight, behavior: "smooth" });
    }
  }, [mensajes, loading, conversacion?._id]);

  const onScrollLista = () => {
    const cont = listaRef.current;
    if (!cont) return;
    distanciaFondoRef.current =
      cont.scrollHeight - cont.scrollTop - cont.clientHeight;
    if (cont.scrollTop < 60 && hayMas && !cargandoMas && onCargarMas) {
      prependRef.current = cont.scrollHeight;
      onCargarMas();
    }
  };

  if (!conversacion) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: WA.bg,
        }}
      >
        {loading ? (
          <Spin tip="Cargando chat…" />
        ) : (
          <Empty
            image={<UserOutlined style={{ fontSize: 44, color: WA.lima }} />}
            description={
              <span style={{ color: WA.textSecondary }}>
                Selecciona una conversación
              </span>
            }
          />
        )}
      </div>
    );
  }

  const ventanaAbierta =
    conversacion.ventanaAbiertaHasta &&
    moment(conversacion.ventanaAbiertaHasta).isAfter(moment());

  const reaccionesPorMensaje = {};
  mensajes.forEach((m) => {
    if (m.tipo === "reaction" && m.contenido?.contextId) {
      if (m.contenido.emoji)
        reaccionesPorMensaje[m.contenido.contextId] = m.contenido.emoji;
      else delete reaccionesPorMensaje[m.contenido.contextId];
    }
  });

  const enviar = () => {
    if (!texto.trim()) return;
    onEnviar(texto.trim());
    setTexto("");
  };

  const cerrarComposer = () => setComposer(null);

  const abrirArchivo = async (urlArchivo) => {
    try {
      const r = await axios.get(urlArchivo, {
        baseURL: "",
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(r.data);
      window.open(blobUrl, "_blank");
    } catch {
      message.error("No se pudo abrir el archivo.");
    }
  };

  const manejarMedia = (formData) => {
    formData.append("conversacionId", conversacion._id);
    onEnviarMedia(formData);
    cerrarComposer();
  };

  const manejarServicio = (payload) => {
    onEnviarServicio({ ...payload, conversacionId: conversacion._id });
    cerrarComposer();
  };

  const manejarPlantilla = (formData) => {
    formData.append("conversacionId", conversacion._id);
    onEnviarPlantilla(formData);
    setSelectorPlantillas(false);
  };

  const abrirComposer = (key) => {
    switch (key) {
      case "image":
        setComposer({
          kind: "media",
          tipo: "image",
          accept: "image/*",
          titulo: "Enviar imagen",
        });
        break;
      case "document":
        setComposer({
          kind: "media",
          tipo: "document",
          accept: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv",
          titulo: "Enviar documento",
        });
        break;
      case "audio":
        setComposer({
          kind: "media",
          tipo: "audio",
          accept: "audio/*",
          titulo: "Enviar audio",
        });
        break;
      case "video":
        setComposer({
          kind: "media",
          tipo: "video",
          accept: "video/*",
          titulo: "Enviar video",
        });
        break;
      case "sticker":
        setComposer({
          kind: "media",
          tipo: "sticker",
          accept: "image/*",
          titulo: "Enviar sticker",
        });
        break;
      case "insumo":
        setComposer({ kind: "insumo" });
        break;
      case "location":
        setComposer({ kind: "ubicacion", modo: "location" });
        break;
      case "location_request":
        setComposer({ kind: "ubicacion", modo: "location_request" });
        break;
      default:
        break;
    }
  };

  // antd 4.15: el Menu usa <Menu.Item> como hijos (la prop `items` es de 4.20+).
  const menuServicio = (
    <Menu onClick={({ key }) => abrirComposer(key)}>
      <Menu.Item key="image" icon={<PictureOutlined />}>
        Imagen
      </Menu.Item>
      <Menu.Item key="document" icon={<FileTextOutlined />}>
        Documento
      </Menu.Item>
      <Menu.Item key="audio" icon={<AudioOutlined />}>
        Audio
      </Menu.Item>
      <Menu.Item key="video" icon={<VideoCameraOutlined />}>
        Video
      </Menu.Item>
      <Menu.Item key="sticker" icon={<SmileOutlined />}>
        Sticker
      </Menu.Item>
      <Menu.Item key="insumo" icon={<ShoppingOutlined />}>
        Detalle de insumo
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="location" icon={<EnvironmentOutlined />}>
        Ubicación
      </Menu.Item>
      <Menu.Item key="location_request" icon={<AimOutlined />}>
        Pedir ubicación
      </Menu.Item>
    </Menu>
  );

  const nombre = nombreConversacion(conversacion);
  const iniciales = inicialesDe(nombre);
  const pastel = colorAvatar(conversacion.telefono);

  return (
    <>
      <style>{`
        .wa-scroll-verde { scrollbar-width: thin; scrollbar-color: ${WA.limaFuerte} transparent; }
        .wa-scroll-verde::-webkit-scrollbar { width: 6px; height: 6px; }
        .wa-scroll-verde::-webkit-scrollbar-track { background: transparent; }
        .wa-scroll-verde::-webkit-scrollbar-thumb { background-color: ${WA.limaFuerte}; border-radius: 8px; }
        .wa-scroll-verde::-webkit-scrollbar-thumb:hover { background-color: ${WA.lima}; }
      `}</style>
      {/* Encabezado del hilo */}
      <div
        style={{
          padding: "10px 16px",
          borderBottom: `1px solid ${WA.border}`,
          backgroundColor: WA.surface,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Avatar
          style={{
            backgroundColor: pastel.fondo,
            color: pastel.texto,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {iniciales || <UserOutlined />}
        </Avatar>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600,
              color: WA.textPrimary,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {nombre}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Text style={{ fontSize: 12, color: WA.textSecondary }}>
              {formatTelefono(conversacion.telefono)}
            </Text>
            {ventanaAbierta ? (() => {
              const vence = moment(conversacion.ventanaAbiertaHasta);
              const horasRestantes = vence.diff(moment(), "hours", true);
              const color = horasRestantes < 2 ? WA.naranja : WA.lima;
              return (
                <Text style={{ fontSize: 11, color, whiteSpace: "nowrap" }}>
                  <ClockCircleOutlined style={{ marginRight: 3 }} />
                  {vence.format("HH:mm")}
                  {" · "}
                  {horasRestantes >= 1
                    ? `${Math.floor(horasRestantes)}h restantes`
                    : `${Math.round(horasRestantes * 60)}min restantes`}
                </Text>
              );
            })() : (
              <Text style={{ fontSize: 11, color: WA.textSecondary, whiteSpace: "nowrap" }}>
                {numeroPorUsuario
                  ? "· Esperando primer contacto, arranca con plantilla"
                  : "· Inicia conversación"}
              </Text>
            )}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {!conversacion.contactoId && onVincularContacto && (
            <ButtonItem
              turquesa
              lessTime
              icon={<UserAddOutlined />}
              tooltip="Vincular a un contacto del CRM"
              onClick={() => setModalVincular(true)}
            />
          )}
          {numeroPorUsuario && puedeDerivar && onDerivar && (
            <ButtonItem
              morado
              lessTime
              icon={<UserSwitchOutlined />}
              tooltip="Pasar este chat a un vendedor"
              onClick={() => setModalPasar(true)}
            />
          )}
        </div>
      </div>

      {/* Mensajes */}
      <div
        ref={listaRef}
        onScroll={onScrollLista}
        className="wa-scroll-verde"
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: 16,
          backgroundColor: WA.bg,
        }}
      >
        {loading ? (
          <div style={{ textAlign: "center", paddingTop: 30 }}>
            <Spin />
          </div>
        ) : (
          <>
            {cargandoMas && (
              <div style={{ textAlign: "center", padding: "4px 0 12px" }}>
                <Spin size="small" />
              </div>
            )}
            {mensajes.map((m) => {

              if (m.tipo === "reaction") return null;

              if (m.tipo === "sistema") {
                return (
                  <div
                    key={m._id}
                    style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        maxWidth: "85%",
                        padding: "4px 12px",
                        borderRadius: 10,
                        backgroundColor: "#F2F4F5",
                        color: "#9AA7AE",
                        fontSize: 11,
                        lineHeight: 1.5,
                        textAlign: "center",
                      }}
                    >
                      <UserSwitchOutlined style={{ flexShrink: 0 }} />
                      <span>
                        {m.texto}
                        {m.createdAt ? ` · ${moment(m.createdAt).format("DD/MM/YY HH:mm")}` : ""}
                      </span>
                    </div>
                  </div>
                );
              }

              const saliente = m.direccion === "saliente";
              const reaccion = reaccionesPorMensaje[m.waMessageId];

              const citado = m.contenido?.contextId
                ? mensajes.find((x) => x.waMessageId === m.contenido.contextId)
                : null;

              const fondo = saliente ? "rgba(201, 223, 0, 0.45)" : WA.bubbleIn;
              const colorTexto = saliente ? WA.bubbleOutText : WA.textPrimary;
              const borde = saliente ? "none" : `1px solid ${WA.border}`;
              return (
                <div
                  key={m._id || m.waMessageId}
                  style={{
                    display: "flex",
                    justifyContent: saliente ? "flex-end" : "flex-start",
                    marginBottom: reaccion ? 18 : 8,
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      maxWidth: "70%",
                      padding: "8px 12px",
                      borderRadius: saliente
                        ? "12px 12px 2px 12px"
                        : "12px 12px 12px 2px",
                      backgroundColor: fondo,
                      color: colorTexto,
                      border: borde,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {reaccion && (
                      <span
                        style={{
                          position: "absolute",
                          bottom: -12,
                          [saliente ? "left" : "right"]: 10,
                          background: WA.surface,
                          border: `1px solid ${WA.border}`,
                          borderRadius: 12,
                          padding: "1px 6px",
                          fontSize: 14,
                          lineHeight: "18px",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                        }}
                      >
                        {reaccion}
                      </span>
                    )}
                    {citado && (
                      <div
                        style={{
                          borderLeft: `3px solid ${WA.turquesa}`,
                          background: "rgba(0,0,0,0.04)",
                          borderRadius: 6,
                          padding: "4px 8px",
                          marginBottom: 6,
                          fontSize: 12,
                        }}
                      >
                        <div style={{ fontWeight: 600, color: WA.turquesa }}>
                          {citado.direccion === "saliente" ? "Tú" : nombre}
                        </div>
                        <div
                          style={{
                            color: WA.textSecondary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 220,
                          }}
                        >
                          {resumenMensaje(citado)}
                        </div>
                      </div>
                    )}
                    <BurbujaContenido m={m} onAbrirArchivo={abrirArchivo} />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        fontSize: 10,
                        color: saliente ? "rgba(0,0,0,0.55)" : WA.textSecondary,
                        marginTop: 2,
                      }}
                    >
                      {moment(m.createdAt).format("HH:mm")}
                      {saliente && <Tick estado={m.estado} errorMensaje={m.errorMensaje} />}
                    </div>
                    {saliente && m.estado === "fallido" && (
                      <div style={{ fontSize: 10, color: "#c0392b", marginTop: 2, textAlign: "right" }}>
                        {m.errorMensaje || "No se pudo entregar"}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Barra de envío */}
      <div
        style={{
          padding: 10,
          borderTop: `1px solid ${WA.border}`,
          backgroundColor: WA.surface,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        {puedeEscribir === false ? (
          <Text style={{ fontSize: 12, color: WA.textSecondary, margin: "0 auto" }}>
            Solo lectura. No tienes permiso para contestar chats de otros.
          </Text>
        ) : ventanaAbierta ? (
          <>
            <Dropdown
              overlay={menuServicio}
              trigger={["click"]}
              placement="topLeft"
              disabled={enviando}
            >
              <Tooltip title="Elementos de servicio">
                <ButtonItem className="botonAgregar" cyan icon={<PaperClipOutlined />} />
              </Tooltip>
            </Dropdown>
            <Tooltip title="Interactivos">
              <ButtonItem
                className="botonAgregar"
                lima
                icon={<AppstoreAddOutlined />}
                onClick={() => setSelectorInteractivos(true)}
                disabled={enviando}
              />
            </Tooltip>
            <Tooltip title="Plantillas">
              <ButtonItem
                className="botonAgregar"
                naranja2
                icon={<SnippetsOutlined />}
                onClick={() => setSelectorPlantillas(true)}
                disabled={enviando}
              />
            </Tooltip>
            <Input
              className="wa-input"
              placeholder="Escribe un mensaje..."
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onPressEnter={enviar}
              disabled={enviando}
              style={{ borderRadius: 20 }}
            />
            <Tooltip title="Enviar">
              <ButtonItem
                className="botonAgregar"
                petroleo
                icon={<SendOutlined />}
                onClick={enviar}
                disabled={enviando || !texto.trim()}
              />
            </Tooltip>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 8,
              backgroundColor: "#FFF4EC",
              border: `1px solid ${WA.naranja}33`,
              width: "100%",
            }}
          >
            <InfoCircleOutlined style={{ color: WA.naranja }} />
            <Text style={{ fontSize: 12, color: WA.textSecondary, flex: 1 }}>
              Fuera de la ventana de 24h. Para escribir necesitas una plantilla aprobada.
            </Text>
            <ButtonItem
              naranja2
              icon={<SnippetsOutlined />}
              onClick={() => setSelectorPlantillas(true)}
              disabled={enviando}
            >
              Usar plantilla
            </ButtonItem>
          </div>
        )}
      </div>

      {/* Composers */}
      <ComposerMedia
        visible={composer?.kind === "media"}
        tipo={composer?.tipo}
        accept={composer?.accept}
        titulo={composer?.titulo}
        headersToken={headersToken}
        empresaId={empresaId}
        onCancel={cerrarComposer}
        onEnviar={manejarMedia}
      />
      <ComposerInsumo
        visible={composer?.kind === "insumo"}
        headersToken={headersToken}
        empresaId={empresaId}
        puedeGuardarImagenInsumo={puedeGuardarImagenInsumo}
        onCancel={cerrarComposer}
        onEnviar={manejarServicio}
      />
      <ComposerUbicacion
        visible={composer?.kind === "ubicacion"}
        modo={composer?.modo}
        headersToken={headersToken}
        empresaId={empresaId}
        onCancel={cerrarComposer}
        onEnviar={manejarServicio}
      />
      <SelectorInteractivos
        visible={selectorInteractivos}
        headersToken={headersToken}
        empresaId={empresaId}
        puedeGuardarPlantilla={puedeGuardarPlantilla}
        onCancel={() => setSelectorInteractivos(false)}
        onEnviar={(payload) => {
          manejarServicio(payload);
          setSelectorInteractivos(false);
        }}
      />
      <SelectorPlantillas
        visible={selectorPlantillas}
        headersToken={headersToken}
        empresaId={empresaId}
        onCancel={() => setSelectorPlantillas(false)}
        onEnviar={manejarPlantilla}
      />

      <ModalVincularContacto
        visible={modalVincular}
        conversacion={conversacion}
        headersToken={headersToken}
        empresaId={empresaId}
        onVincular={(contactoId, cerrar) =>
          onVincularContacto(conversacion._id, contactoId, () => cerrar?.())
        }
        onCancel={() => setModalVincular(false)}
      />

      <ModalPasarChat
        visible={modalPasar}
        conversacion={conversacion}
        headersToken={headersToken}
        onPasar={(conversacionId, usuarioId, mensaje, limpiar) =>
          onDerivar(conversacionId, usuarioId, mensaje, () => {
            limpiar?.();
            setModalPasar(false);
          })
        }
        onCancel={() => setModalPasar(false)}
      />
    </>
  );
};

export default HiloMensajes;
